import React from 'react';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

const cardVariants = cva(
  // Base styles
  'relative rounded-lg transition-all duration-200',
  {
    variants: {
      variant: {
        default: 'bg-gray-900 border border-gray-800 hover:border-gray-700',
        glass: 'bg-gray-900/40 backdrop-blur-md border border-white/10 hover:border-white/20',
        solid: 'bg-gray-800 shadow-lg hover:shadow-xl',
        gradient:
          'bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-800 hover:border-gray-700',
      },
      size: {
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-6',
      },
      hover: {
        none: '',
        lift: 'hover:transform hover:-translate-y-1',
        glow: 'hover:shadow-[0_0_15px_rgba(255,215,0,0.1)]',
        highlight: 'hover:border-yellow-600/50',
      },
      interactive: {
        true: 'cursor-pointer',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      hover: 'none',
      interactive: false,
    },
  }
);

export const Card = React.forwardRef(
  ({ className, variant, size, hover, interactive, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={twMerge(cardVariants({ variant, size, hover, interactive }), className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={twMerge('flex flex-col space-y-1.5 pb-4', className)} {...props}>
    {children}
  </div>
));

CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h3
    ref={ref}
    className={twMerge('font-semibold leading-none tracking-tight text-white', className)}
    {...props}
  >
    {children}
  </h3>
));

CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <p ref={ref} className={twMerge('text-sm text-gray-400', className)} {...props}>
    {children}
  </p>
));

CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={twMerge('', className)} {...props}>
    {children}
  </div>
));

CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={twMerge('flex items-center pt-4', className)} {...props}>
    {children}
  </div>
));

CardFooter.displayName = 'CardFooter';
